/**
 * SLPipeline — async SmartLayer processing pipeline.
 *
 * All SmartLayer computation runs in a Web Worker so the UI thread
 * never freezes during product imports.
 *
 * Products are processed sequentially (not concurrently) to prevent
 * worker queue overload and progressive failure.
 */

import { optimizeProductData } from '../services/intelligence';

const DEFAULT_SETTINGS = {
  platform: 'ebay',
  targetMargin: 0.42,
  platformFeeEstimate: 0.14,
  paymentFeeEstimate: 0.035,
  adBufferRate: 0.12,
  minProfitDollars: 5,
  minPublishScore: 78,
  strictCompliance: true,
};

function loadSLSettings() {
  try {
    const raw = localStorage.getItem('amorlia_smartlayer_settings');
    if (raw) return JSON.parse(raw);
  } catch {}
  return { ...DEFAULT_SETTINGS };
}

// ── Pre-processing helpers ──────────────────────────────────────────────────────

function stripHtml(str) {
  if (!str) return '';
  return str
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s{3,}/g, '\n')
    .trim();
}

const SUPPLIER_NOISE_PATTERNS = [
  /Item\s*Id?\s*:\s*[A-Z0-9\-]+/gi,
  /Quantity\s*:\s*\d+\s*(Piece|Pieces|Set|Sets|Lot|PC|PCS)?/gi,
  /Classification\s*:\s*[^,.\n;]+/gi,
  /Item\s*No\s*\.?\s*:\s*[A-Z0-9\-]+/gi,
  /Product\s*(No|Code|Number)\s*\.?\s*:\s*[A-Z0-9\-]+/gi,
  /Order\s*Id?\s*:\s*[A-Z0-9\-]+/gi,
  /Goods\s*No\s*\.?\s*:\s*[A-Z0-9\-]+/gi,
  /NH[A-Z]{0,3}\d{5,}/g,
  /Reseller\s+Business\s+Benefit/gi,
  /1-6FREE\s+SHIPPING/gi,
  /Clearance\s*Sale/gi,
  /Wholesale\s+(Price|Jewelry|Fashion)/gi,
  /Min(?:imum)?\s+Order\s*:\s*\d+/gi,
  /MOQ\s*:\s*\d+/gi,
  /Drop\s*[Ss]hipping/gi,
  /Factory\s+(Price|Direct)/gi,
  /(?:Nihaojewelry|Nihao Jewelry|NIHAO)/gi,
  /(?:AliExpress|Alibaba|DHgate|Temu|Shein|CJ Dropshipping)/gi,
];

function removeSupplierNoise(text) {
  if (!text) return '';
  let cleaned = text;
  for (const pattern of SUPPLIER_NOISE_PATTERNS) {
    cleaned = cleaned.replace(pattern, '');
  }
  return cleaned.replace(/[,;]\s*[,;]/g, ',').replace(/\s{2,}/g, ' ').trim();
}

export function cleanRawTitle(raw) {
  if (!raw) return '';
  let title = stripHtml(raw);
  title = removeSupplierNoise(title);
  const fragments = ['and', 'or', 'with', 'for', 'of', 'in', 'the', 'a', 'an', 'to', 'from', 'by', ',', ';', '-'];
  const words = title.trim().split(/\s+/);
  while (words.length > 1 && fragments.includes(words[words.length - 1].toLowerCase().replace(/[^a-z]/g, ''))) {
    words.pop();
  }
  return words.join(' ').slice(0, 80).trim();
}

export function cleanRawDescription(raw) {
  if (!raw) return '';
  let desc = stripHtml(raw);
  desc = removeSupplierNoise(desc);
  desc = desc
    .replace(/font-family[^;]+;?/gi, '')
    .replace(/line-height[^;]+;?/gi, '')
    .replace(/max-width[^;]+;?/gi, '')
    .replace(/color:#[0-9a-fA-F]+;?/gi, '');
  return desc.replace(/\s{3,}/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function cleanRawSku(raw) {
  if (!raw) return '';
  if (raw.includes(',') || raw.includes(':')) {
    const firstSegment = raw.split(/[,:\n]/)[0].trim();
    if (/^[A-Z0-9\-]{4,25}$/i.test(firstSegment)) return firstSegment;
    return '';
  }
  return raw.replace(/Quantity/gi, '').replace(/Piece/gi, '').replace(/Classification/gi, '').trim().slice(0, 30);
}

export function smartCleanVariantName(rawName, fullDescription = '', productTitle = '', productType = 'jewelry', index = 0) {
  if (!rawName) return `Design ${index + 1}`;
  
  let name = String(rawName).trim();
  
  // Keep original if it's already a good name
  if (/^[A-Za-z\s]+$/.test(name) && name.length > 2) return name;
  
  // Strip all known SKU patterns (JDRW, JDR, NH, etc.)
  name = name.replace(/^(JDRW?|JDR|NH|#)\d+[-_]?[A-Z0-9-]*$/i, '').trim();
  name = name.replace(/[-_]/g, ' ').trim();
  
  // Remove leftover junk
  name = name.replace(/\b(Original Version|1:1|Steel Color|Quantity|Piece|Classification)\b/gi, '').trim();
  
  if (name.length < 2 || /^\d+$/.test(name)) {
    // Smart fallback from description or title, but only if it's a single variant or we append index
    const colorMatch = (fullDescription + ' ' + productTitle).match(
      /\b(Red|Blue|Gold|Silver|Black|White|Green|Pink|Purple|Brown|Gray|Steel|Rose Gold|Yellow Gold|Tiger Stone|Lapis Lazuli|Geometric|Eye|Pendant|Ring|Cross|Heart|Butterfly|Moon|Star|Pearl)\b/i
    );
    if (colorMatch) {
      name = `${colorMatch[1]} Design ${index + 1}`;
    } else {
      name = `Style ${index + 1}`;
    }
  }
  
  // Capitalize nicely
  name = name.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  
  return name;
}

function parseSourcePrice(raw) {
  if (raw === null || raw === undefined) return 0;
  if (typeof raw === 'number') return raw > 0 ? raw : 0;
  const str = String(raw).replace(/US\$|USD|\$|,/g, '').trim();
  const rangeMatch = str.match(/(\d+\.?\d*)\s*[-–]\s*\d+/);
  if (rangeMatch) return parseFloat(rangeMatch[1]);
  const singleMatch = str.match(/(\d+\.?\d*)/);
  if (singleMatch) return parseFloat(singleMatch[1]);
  return 0;
}

function mapItemSpecifics(raw) {
  const rawSpecs = raw.itemSpecifics || raw.specs || raw.attributes || {};
  const title = (raw.title || '').toLowerCase();
  const desc = cleanRawDescription(raw.description || '');

  const detectMaterial = () => {
    const search = title + ' ' + desc + ' ' + (rawSpecs.material || rawSpecs.Material || '');
    if (/316l|surgical stainless/i.test(search)) return '316L Stainless Steel';
    if (/stainless steel|304 stainless/i.test(search)) return 'Stainless Steel';
    if (/sterling silver|925|s925/i.test(search)) return '925 Sterling Silver';
    if (/gold filled|14k gf|18k gf/i.test(search)) return 'Gold Filled';
    if (/gold plated|18k plated|14k plated/i.test(search)) return 'Gold Plated';
    if (/silver plated/i.test(search)) return 'Silver Plated';
    if (/alloy|zinc alloy/i.test(search)) return 'Metal Alloy';
    if (/copper/i.test(search)) return 'Copper';
    return rawSpecs.material || rawSpecs.Material || 'Stainless Steel';
  };

  const detectMetal = () => {
    const search = title + ' ' + (rawSpecs.metal || rawSpecs.Metal || '');
    if (/rose gold/i.test(search)) return 'Rose Gold Tone';
    if (/gold/i.test(search)) return 'Gold Tone';
    if (/silver/i.test(search)) return 'Silver Tone';
    if (/black/i.test(search)) return 'Black';
    return rawSpecs.metal || rawSpecs.Metal || 'Gold Tone';
  };

  const detectType = () => {
    if (/necklace|pendant|chain/i.test(title)) return 'Necklace';
    if (/bracelet|bangle|cuff/i.test(title)) return 'Bracelet';
    if (/earring|stud|hoop|huggie/i.test(title)) return 'Earrings';
    if (/ring(?! light)/i.test(title)) return 'Ring';
    if (/anklet/i.test(title)) return 'Anklet';
    return rawSpecs.type || rawSpecs.Type || 'Jewelry';
  };

  const extractLength = () => {
    const search = title + ' ' + desc + ' ' + (rawSpecs.length || rawSpecs.Length || '');
    const inchMatch = search.match(/(\d{1,3}(?:\.\d+)?)\s*(?:in|inch|inches|")/i);
    if (inchMatch) return `${inchMatch[1]} in`;
    const cmMatch = search.match(/(\d{1,3}(?:\.\d+)?)\s*cm/i);
    if (cmMatch) return `${cmMatch[1]} cm`;
    return rawSpecs.length || rawSpecs.Length || '';
  };

  const detectStyle = () => {
    if (/minimal|dainty|delicate|simple/i.test(title)) return 'Minimalist';
    if (/heart|love|romantic/i.test(title)) return 'Heart';
    if (/initial|letter|monogram/i.test(title)) return 'Personalized';
    if (/butterfly|flower|floral/i.test(title)) return 'Nature Inspired';
    if (/cross|faith|prayer/i.test(title)) return 'Faith Inspired';
    if (/evil eye|hamsa|protection/i.test(title)) return 'Spiritual';
    return rawSpecs.style || rawSpecs.Style || 'Classic';
  };

  const material = detectMaterial();
  const metal = detectMetal();
  const type = detectType();
  const length = extractLength();
  const style = detectStyle();

  const NOISE_VALUES = [
    /reseller/i, /business benefit/i, /clearance/i, /free shipping/i,
    /1-6free/i, /wholesale/i, /dropship/i, /factory/i, /lot/i, /bulk/i,
    /minimum order/i, /MOQ/i, /sample/i,
  ];

  function cleanSpecValue(val) {
    if (!val) return '';
    const str = String(val);
    for (const pattern of NOISE_VALUES) {
      if (pattern.test(str)) return '';
    }
    return removeSupplierNoise(str).trim();
  }

  return {
    Brand: cleanSpecValue(rawSpecs.brand || rawSpecs.Brand) || 'Unbranded',
    Material: material,
    Metal: metal,
    Style: style,
    Type: type,
    Occasion: cleanSpecValue(rawSpecs.occasion || rawSpecs.Occasion) || 'Casual, Gift',
    Length: length || 'See description',
    'Country/Region of Manufacture': 'China',
    Condition: 'New',
    Returns: '30 Day Returns',
  };
}

// Normalize raw variants into a consistent selectable structure
function normalizeVariants(rawVariants) {
  if (!Array.isArray(rawVariants) || rawVariants.length === 0) return [];

  return rawVariants.map((v, i) => {
    const vTitle = cleanRawTitle(v.title || v.name || v.option || v.value || `Option ${i + 1}`);
    const price = parseSourcePrice(v.price || v.cost || 0);
    const sku = cleanRawSku(v.sku || v.id || '');
    const images = Array.isArray(v.images) ? v.images.filter(Boolean) : v.image ? [v.image] : [];

    return {
      id: sku || `var_${i}_${Date.now()}`,
      title: vTitle || `Option ${i + 1}`,
      price,
      sku,
      images,
      image: images[0] || '', // Added for compatibility with frontend expecting v.image
      available: v.available !== false,
      _raw: v,
    };
  }).filter(v => v.title);
}

async function runBadgeEnrichment(product, settings) {
  try {
    const [precheckResult, cardResult, detailResult] = await Promise.all([
      callSmartLayer('runSmartLayerFastPrecheck', product, settings),
      callSmartLayer('buildSmartLayerDashboardCardV2', product, settings),
      callSmartLayer('buildSmartLayerProductDetailPagePack', product, settings),
    ]);
    return {
      precheck: precheckResult || null,
      card: cardResult || null,
      detail: detailResult || null,
    };
  } catch (_) {
    return null;
  }
}

// ── Main processing ──────────────────────────────────────────────────────────────

export function calculateTargetPrice(rawCost, options = {}) {
  if (!rawCost || rawCost <= 0) return 0;
  const shipping = options.shipping || 4.00;
  const feesPct = 0.1325;
  const fixedFee = 0.30;
  const targetProfit = 5.00;
  const totalCost = parseFloat(rawCost) + shipping;
  let price = (totalCost + fixedFee + targetProfit) / (1 - feesPct);
  
  // Psychology rounding
  price = Math.ceil(price * 100) / 100;
  const decimal = price % 1;
  if (decimal < 0.5) price = Math.floor(price) + 0.99;
  else price = Math.ceil(price);
  
  // Safety floor
  const minSafe = parseFloat(rawCost) * 3;
  return Math.max(Math.round(price * 100) / 100, minSafe);
}

export async function processSingleProduct(rawProduct) {
  const settings = loadSLSettings();

  // Step 1: Pre-clean the raw data BEFORE passing to SmartLayer
  const cleanedRaw = {
    ...rawProduct,
    _rawTitle: (rawProduct._rawTitle || rawProduct.title || rawProduct.name || '').trim(),
    id: rawProduct.id || `sl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    title: cleanRawTitle(rawProduct.title || rawProduct.name || ''),
    description: cleanRawDescription(rawProduct.description || ''),
    sku: cleanRawSku(rawProduct.sku || rawProduct.supplierSku || ''),
    supplierSku: cleanRawSku(rawProduct.sku || rawProduct.supplierSku || ''),
    price: parseSourcePrice(rawProduct.price || rawProduct.rawNihaoPrice || 0),
    sourceUrl: rawProduct.sourceUrl || rawProduct._url || '',
    images: (rawProduct.images || []).filter(img => img && typeof img === 'string' && img.startsWith('http')),
    itemSpecifics: mapItemSpecifics(rawProduct),
    variants: normalizeVariants(rawProduct.variants || []),
  };

  // Development debug logging
  if (process.env.NODE_ENV === 'development') {
    console.group(`[SLPipeline] Processing: "${cleanedRaw.title.slice(0, 50)}"`);
    console.log('  Source price:', cleanedRaw.price);
    console.log('  Description length:', cleanedRaw.description.length, 'chars');
    console.log('  Has HTML:', /<[^>]+>/.test(cleanedRaw.description));
    console.log('  SKU:', cleanedRaw.supplierSku);
    console.log('  Images:', cleanedRaw.images.length);
    console.log('  Variants:', cleanedRaw.variants.length);
    console.groupEnd();
  }

  // Step 2: Run Intelligence Engine on CLEAN data
  let intelResult = {};
  try {
    intelResult = await optimizeProductData(cleanedRaw);
  } catch (e) {
    console.error('[SLPipeline] optimizeProductData failed:', e.message);
    return {
      ...cleanedRaw,
      _raw: rawProduct,
      slStatus: 'BLOCKED_FIX_REQUIRED',
      slOk: false,
      slError: e.message,
      slProcessedAt: new Date().toISOString(),
      processingStatus: 'done',
      smartLayerProcessed: true,
      title: cleanedRaw.title || 'Untitled Product',
      description: cleanedRaw.description,
      tags: [],
      itemSpecifics: cleanedRaw.itemSpecifics,
      priceSuggestion: 0,
      images: cleanedRaw.images,
      sourceUrl: cleanedRaw.sourceUrl,
      supplierSku: cleanedRaw.supplierSku,
      titleVariants: [],
      activeTitleIndex: 0,
      slPlatforms: {},
      slConversion: {
        score: { score: 0, grade: 'F' },
        publishGate: { status: 'BLOCKED_FIX_REQUIRED', canPublish: false, blockers: [e.message], warnings: [] },
        nextFixes: [],
        compliance: { critical: [], major: [], risks: [] },
        pricing: {},
      },
      slDetection: {},
      slOrderEntry: {},
    };
  }

  if (intelResult && intelResult.success && intelResult.data) {
    cleanedRaw._sl = {
      title: intelResult.data.title || cleanedRaw.title || rawProduct.title?.slice(0, 80) || '',
      description: intelResult.data.description || cleanedRaw.description || '',
      itemSpecifics: intelResult.data.itemSpecifics || {},
      tags: intelResult.data.tags || [],
      _log: intelResult.data._intelligenceLog || {}
    };
  }

  // Calculate suggested price based on strategy
  const suggestedPrice = calculateTargetPrice(cleanedRaw.price, settings);

  return {
    ...cleanedRaw,
    _raw: rawProduct,

    slStatus: intelResult.success ? 'READY_TO_REVIEW' : 'NEEDS_OPTIMIZATION',
    slOk: intelResult.success,
    slProcessedAt: new Date().toISOString(),
    processingStatus: 'done',
    smartLayerProcessed: true,

    title: (cleanedRaw._sl?.title?.trim() || cleanedRaw._rawTitle?.slice(0, 80) || cleanedRaw.title?.trim() || 'Untitled Product').slice(0, 80),
    description: cleanedRaw._sl?.description?.trim() || cleanedRaw.description || cleanedRaw._rawTitle || '',
    tags: cleanedRaw._sl?.tags || [],
    itemSpecifics: cleanedRaw._sl?.itemSpecifics || cleanedRaw.itemSpecifics || {},
    images: cleanedRaw.images,
    sourceUrl: cleanedRaw.sourceUrl,
    supplierSku: cleanedRaw.supplierSku,

    titleVariants: [cleanedRaw._sl?.title || cleanedRaw.title || 'Untitled Product'],
    activeTitleIndex: 0,

    priceSuggestion: suggestedPrice,
    price: suggestedPrice > 0 ? suggestedPrice : cleanedRaw.price,
    rawNihaoPrice: cleanedRaw.price,
    sourcePrice: cleanedRaw.price,
    priceConfirmed: true,

    slPlatforms: {},
    slConversion: {
      score: { score: intelResult.success ? 90 : 50 }
    },
    slDetection: {},
    slOrderEntry: {},
    
    _intelligenceLog: intelResult.data?._intelligenceLog,

    variants: (cleanedRaw.variants || []).map((v, i) => {
      const cleanName = smartCleanVariantName(v.name || v.title || v.color, cleanedRaw.description, cleanedRaw.title, cleanedRaw.itemSpecifics?.Type, i);
      
      const vSourcePrice = v.price || 0;
      const vSuggestedPrice = calculateTargetPrice(vSourcePrice, settings);
      
      return { 
        ...v, 
        name: cleanName, 
        title: cleanName,
        price: vSuggestedPrice, // Selling price
        rawNihaoPrice: vSourcePrice, // Source cost
        sourcePrice: vSourcePrice, // Source cost
      };
    }),
  };
}

/**
 * Process products sequentially, updating UI after each one completes.
 * Accepts an optional onProductDone callback for progressive updates.
 */
export async function processBatch(rawProducts, onProductDone = null) {
  if (!Array.isArray(rawProducts) || rawProducts.length === 0) return [];

  const results = [];

  for (const raw of rawProducts) {
    try {
      const result = await processSingleProduct(raw);
      results.push(result);
      if (onProductDone) onProductDone(result);
    } catch (e) {
      console.error('[SLPipeline] Failed to process product:', raw.title, e.message);
      const fallback = {
        ...(raw.id ? raw : { ...raw, id: `sl_fallback_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }),
        _raw: raw,
        slStatus: 'BLOCKED_FIX_REQUIRED',
        slOk: false,
        slError: e.message,
        slProcessedAt: new Date().toISOString(),
        processingStatus: 'done',
        smartLayerProcessed: true,
        title: cleanRawTitle(raw.title || raw.name || 'Untitled Product'),
        description: cleanRawDescription(raw.description || ''),
        tags: [],
        itemSpecifics: mapItemSpecifics(raw),
        priceSuggestion: 0,
        images: (raw.images || []).filter(img => img && typeof img === 'string' && img.startsWith('http')),
        sourceUrl: raw.sourceUrl || raw._url || '',
        supplierSku: cleanRawSku(raw.sku || raw.supplierSku || ''),
        titleVariants: [],
        activeTitleIndex: 0,
        price: parseSourcePrice(raw.price || raw.rawNihaoPrice || 0),
        rawNihaoPrice: parseSourcePrice(raw.price || raw.rawNihaoPrice || 0),
        sourcePrice: parseSourcePrice(raw.price || raw.rawNihaoPrice || 0),
        priceConfirmed: parseSourcePrice(raw.price || raw.rawNihaoPrice || 0) > 0,
        slPlatforms: {},
        slConversion: {
          score: { score: 0, grade: 'F' },
          publishGate: { status: 'BLOCKED_FIX_REQUIRED', canPublish: false, blockers: [e.message], warnings: [] },
          nextFixes: [],
          compliance: { critical: [], major: [], risks: [] },
          pricing: {},
        },
        slDetection: {},
        slOrderEntry: {},
        variants: normalizeVariants(raw.variants || []),
      };
      results.push(fallback);
      if (onProductDone) onProductDone(fallback);
    }
  }

  return results;
}

export async function reprocessProduct(processedVariant, priceOverride = null) {
  const raw = processedVariant._raw || processedVariant;
  const rawToReprocess = {
    ...raw,
    price: priceOverride !== null ? priceOverride : raw.price,
  };
  const reprocessed = await processSingleProduct(rawToReprocess);
  reprocessed.id = processedVariant.id;
  return reprocessed;
}

export function swapTitle(processedVariant, targetIndex) {
  const variants = processedVariant.titleVariants || [];
  if (variants.length === 0 || targetIndex == null) return processedVariant;

  const safeIndex = targetIndex % variants.length;
  return {
    ...processedVariant,
    title: variants[safeIndex],
    activeTitleIndex: safeIndex,
  };
}

export function repriceProduct(processedVariant, newSellingPrice) {
  const pricing = processedVariant.slConversion?.pricing || {};
  const sourcePrice = pricing.supplierCost || processedVariant.rawNihaoPrice || 0;
  const shippingCost = pricing.shippingCost || 4.00;
  const ebayFeeRate = pricing.estimatedFeeRate || 0.1325;
  const processingRate = pricing.paymentFeeRate || 0.03;

  // Guard against zero prices producing impossible margins
  const canCalculate = sourcePrice > 0 && newSellingPrice > 0;
  const estimatedFees = canCalculate ? (newSellingPrice * ebayFeeRate + newSellingPrice * processingRate + 0.35) : 0;
  const netProfit = canCalculate ? (newSellingPrice - sourcePrice - shippingCost - estimatedFees) : null;
  const margin = canCalculate && newSellingPrice > 0 ? (netProfit / newSellingPrice) * 100 : null;

  return {
    ...processedVariant,
    price: newSellingPrice,
    slConversion: {
      ...processedVariant.slConversion,
      pricing: {
        ...pricing,
        overridePrice: newSellingPrice,
        overrideProfit: netProfit !== null ? Math.round(netProfit * 100) / 100 : null,
        overrideMargin: margin !== null ? Math.round(margin * 10) / 10 : null,
        isOverridden: true,
      },
    },
  };
}

export function switchPlatform(processedVariant, platform) {
  const platformOutput = processedVariant.slPlatforms?.[platform];
  if (!platformOutput) return null;

  return {
    ...processedVariant,
    title: platformOutput.title || processedVariant.title,
    description: platformOutput.description || processedVariant.description,
    tags: platformOutput.tags || processedVariant.tags,
    itemSpecifics: platformOutput.itemSpecifics || processedVariant.itemSpecifics,
    voice: platform === 'ebay' ? 'factual' : platform === 'etsy' ? 'gifting' : platform === 'shopify' ? 'stackable' : processedVariant.voice,
    currentPlatform: platform,
  };
}
